/**
 * Removes fixed Cloudinary width/height transforms (`w_*`, `h_*`) from a URL.
 * Other transforms (`f_auto`, `q_auto`, `c_fill`, …) are left intact.
 *
 * @example
 * stripCloudinaryDimensions(
 *   'https://assets-cf.armani.com/image/upload/f_auto,q_auto:good,w_614,h_780,c_fill/v1/file.jpg'
 * )
 * // → '.../upload/f_auto,q_auto:good,c_fill/v1/file.jpg'
 */
export const stripCloudinaryDimensions = (url: string): string => {
  if (!url.includes('/upload/')) return url

  return url.replace(
    /(\/(?:image|video)\/upload\/)([^/]+)/,
    (match, prefix: string, segment: string) => {
      if (!/(?:^|,)(?:w|h)_\d+/.test(segment)) return match

      const cleaned = segment
        .split(',')
        .filter(part => !/^(?:w|h)_\d+$/.test(part))
        .join(',')

      // Drop trailing slash when the transform segment is empty to avoid `upload//v…`
      return cleaned ? `${prefix}${cleaned}` : prefix.replace(/\/$/, '')
    }
  )
}

type MediaAsset = {
  URL?: string
  ImageURL?: string
  [key: string]: unknown
}

type XgenRecommendationProduct = {
  assets?: MediaAsset[]
  images?: MediaAsset[]
  image?: string
  image_alt?: string
  custom_attribute_1?: string[]
  variants?: Array<{ image?: string; [key: string]: unknown }>
  [key: string]: unknown
}

const stripAssetDimensions = <T extends MediaAsset>(asset: T): T => {
  const next = { ...asset }
  if (typeof next.URL === 'string') {
    next.URL = stripCloudinaryDimensions(next.URL)
  }
  if (typeof next.ImageURL === 'string') {
    next.ImageURL = stripCloudinaryDimensions(next.ImageURL)
  }
  return next
}

/**
 * Strips Cloudinary `w_*` / `h_*` from image/video URLs on an Xgen recommendation product
 * (Algolia-shaped assets/images and raw Xgen image fields).
 */
export const stripCloudinaryDimensionsFromProduct = <
  T extends XgenRecommendationProduct,
>(
  product: T
): T => {
  const next: XgenRecommendationProduct = { ...product }

  if (Array.isArray(next.assets)) {
    next.assets = next.assets.map(stripAssetDimensions)
  }
  if (Array.isArray(next.images)) {
    next.images = next.images.map(stripAssetDimensions)
  }
  if (typeof next.image === 'string') {
    next.image = stripCloudinaryDimensions(next.image)
  }
  if (typeof next.image_alt === 'string') {
    next.image_alt = stripCloudinaryDimensions(next.image_alt)
  }
  if (Array.isArray(next.custom_attribute_1)) {
    next.custom_attribute_1 = next.custom_attribute_1.map(item =>
      typeof item === 'string' ? stripCloudinaryDimensions(item) : item
    )
  }
  if (Array.isArray(next.variants)) {
    next.variants = next.variants.map(variant =>
      typeof variant?.image === 'string'
        ? { ...variant, image: stripCloudinaryDimensions(variant.image) }
        : variant
    )
  }

  return next as T
}
